import { Injectable, inject } from '@angular/core';

import { BetFinderConfig, BetslipConfig, EpcotConfig, PopupsConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DEFAULT_HEADER_SELECTOR, DialogAnimation } from '@frontend/sports/modal/feature';
import { PopupManager } from '@frontend/sports/popup/feature';
import { DeviceService, TimerService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';

import { BetslipResultActions } from '../../betslip/modules/result/actions';
import { DigitalSettingsActions } from '../../betslip/modules/settings/digital-actions';
import { BetModalComponentLoaderService } from '../bet-modal/bet-modal-component-loader.service';
import { BetfinderLoaderService } from '../betfinder-integration/betfinder-preloader.feature';
import { BetslipDigitalComponentLoaderService } from '../betslip/betslip-digital-component-loader.service';
import { EventSwitcherComponentLoaderService } from '../event-details/event-switcher-component-loader.service';
import { MyBetsComponentLoaderService } from '../my-bets/my-bets-component-loader.service';
import { ExternalNativeAppService } from '../native-api/external-native-app.service';
import { RunAfterLcpService } from '../perf-cdk/run-after-lcp.service';

@Injectable({
    providedIn: 'root',
})
export class PopupInitializationService {
    private deviceService = inject(DeviceService);
    private popupManager = inject(PopupManager);
    private popupConfig = inject(PopupsConfig);
    private sitecore = inject(Sitecore);
    private externalNativeAppService = inject(ExternalNativeAppService);
    private betslipLoader = inject(BetslipDigitalComponentLoaderService);
    private myBetsLoader = inject(MyBetsComponentLoaderService);
    private betslipConfig = inject(BetslipConfig);
    private eventSwitcherLoader = inject(EventSwitcherComponentLoaderService);
    private timerService = inject(TimerService);
    private store = inject(Store);
    private runAfterLcp = inject(RunAfterLcpService);
    private epcotConfig = inject(EpcotConfig);
    private betFinderConfig = inject(BetFinderConfig);
    private betModalComponentLoaderService = inject(BetModalComponentLoaderService);

    private readonly betfinderLoader = inject(BetfinderLoaderService);

    private popupDict = {
        'bet-generator': () =>
            import('../bet-generator-shared/modal/bet-generator-shared-modal.component').then(
                ({ BetGeneratorSharedModalComponent }) => BetGeneratorSharedModalComponent,
            ),
        'bet-builder-overlay': () =>
            import('../bet-builder/components/market-list/bet-builder-market-list.component').then(
                ({ BetbuilderMarketListComponent }) => BetbuilderMarketListComponent,
            ),
        'grouped-sports': () => import('../sport-list/sport-list-modal.component').then(({ SportListModalComponent }) => SportListModalComponent),
        'sports': () => import('../sport-list/sport-list-modal.component').then(({ SportListModalComponent }) => SportListModalComponent),
        'archived-bets': () =>
            import('../archived-bets/archived-bets-modal.component').then(({ ArchivedBetsModalComponent }) => ArchivedBetsModalComponent),
        'transaction-bets': () =>
            import('../archived-bets/archived-bets-modal.component').then(({ ArchivedBetsModalComponent }) => ArchivedBetsModalComponent),
        'ngrx-reports': () => import('../ngrx-reports/components/ngrx-reports.component').then(({ NgrxReportsComponent }) => NgrxReportsComponent),
        'betslip': () => import('@frontend/sports/popup/feature').then(({ PopupProxyComponent }) => PopupProxyComponent),
        'my-bets': () => import('@frontend/sports/popup/feature').then(({ PopupProxyComponent }) => PopupProxyComponent),
        'event-switcher': () => import('@frontend/sports/popup/feature').then(({ PopupProxyComponent }) => PopupProxyComponent),
        'bet-modal': () => import('@frontend/sports/popup/feature').then(({ PopupProxyComponent }) => PopupProxyComponent),
    };

    async onAppInit(): Promise<void> {
        const epcotClass = this.epcotConfig.isEnabled ? 'epcot-enabled' : '';

        const betslipComponent = this.betslipLoader.getBetslipComponent();
        betslipComponent.update({ inColumn: false });

        this.popupManager.configure('bet-generator', this.popupDict['bet-generator'], {
            settings: {
                cssClass: 'modal-popup bet-generator-modal',
                showPageHeader: true,
                showDialogHeader: true,
                title: this.sitecore.multiRandomGenerator.Title,
                fit: false,
                hideCloseButton: false,
                animation: this.getAnimation('betslip'),
                isModalDialog: true,
            },
        });
        this.popupManager.configure('bet-builder-overlay', this.popupDict['bet-builder-overlay'], {
            settings: {
                backdropClass: 'betbuilder-overlay-backdrop',
                showDialogHeader: true,
                animation: DialogAnimation.SlideInFromBottom,
            },
        });
        this.popupManager.configure('grouped-sports', this.popupDict['grouped-sports'], {
            settings: {
                cssClass: 'modal-popup sports',
                showPageHeader: false,
                showDialogHeader: true,
                title: this.sitecore.event.AZsports,
                hideCloseButton: false,
                isModalDialog: true,
            },
        });

        this.popupManager.configure('sports', this.popupDict.sports, {
            settings: {
                cssClass: 'az-menu',
                showPageHeader: false,
                showDialogHeader: false,
                title: this.sitecore.event.AZsports,
                hideCloseButton: false,
                isModalDialog: true,
                closeOnChildPopup: true,
                backdropClass: 'az-desktop-backdrop',
                scrollFreezeType: ScrollFreezeServiceTypes.All,
            },
        });

        this.popupManager.configure('archived-bets', this.popupDict['archived-bets'], {
            settings: {
                cssClass: 'modal-popup sports',
                showDialogHeader: true,
                title: this.sitecore.betslip.archivedBetsHelpBox.HeaderLine,
                hideCloseButton: false,
                isModalDialog: true,
            },
        });
        this.popupManager.configure('transaction-bets', this.popupDict['transaction-bets'], {
            data: {
                showTransactionBetsDialog: true,
            },
            settings: {
                cssClass: 'modal-popup sports',
                showDialogHeader: true,
                title: this.sitecore.betslip.transactionBetsHelpBox.HeaderLine,
                hideCloseButton: false,
                isModalDialog: true,
            },
        });

        this.popupManager.configure('ngrx-reports', this.popupDict['ngrx-reports'], {
            settings: {
                cssClass: 'modal-popup sports',
                showDialogHeader: true,
                title: this.sitecore.ngrxReports.messages.ReportsTitle,
                hideCloseButton: false,
                isModalDialog: true,
            },
        });

        this.popupManager.configure('betfinder', () => this.betfinderLoader.load(), {
            settings: {
                cssClass: 'modal-popup betfinder-modal',
                fit: true,
                showPageHeader: false,
                animation: this.getAnimation('betfinder'),
                showDialogHeader: this.shouldShowDialogHeader(),
                showBottomNav: true,
                title: this.sitecore.betFinder.SearchTitle,
                hideCloseButton: false,
                isModalDialog: false,
                closeAnimation: this.epcotConfig.isEnabled && this.deviceService.isMobile ? DialogAnimation.SlideOutFromTop : undefined,
                gestures: true,
                scrollTargetQuerySelector: '.popular-recent-search-suggestion',
                headerSelector: '.search-bar',
            },
        });

        const betslipClass = this.betslipConfig.isBalanceHeaderEnabled ? 'balance-header-visible' : '';

        this.popupManager.configure('betslip', this.popupDict.betslip, {
            data: {
                proxy: betslipComponent,
            },
            settings: {
                cssClass: `modal-popup betslip-popup ${epcotClass} ${betslipClass}`,
                showPageHeader: !this.externalNativeAppService.isPokerNativeApp() && !this.betslipConfig.isBalanceHeaderEnabled,
                showDialogHeader: !this.betslipConfig.isBalanceHeaderEnabled,
                title: this.sitecore.betslip.messages.Title,
                hideCloseButton: false,
                animation: this.getAnimation('betslip'),
                isModalDialog: true,
                epcotShowBalance: true,
                backdropClass: `betslip-backdrop hide-background-overlay ${betslipClass}`,
                scrollTargetQuerySelector: '.betslip-scroller',
                closePrevious: true,
                afterResulted: () => {
                    this.store.dispatch(BetslipResultActions.close());
                    this.store.dispatch(DigitalSettingsActions.closeSettings());
                },
                headerSelector: 'bs-digital-balance-header',
                gestures: true,
            },
        });
        this.popupManager.configure('my-bets', this.popupDict['my-bets'], {
            data: {
                proxy: this.myBetsLoader.getColumnViewComponent(),
            },
            settings: {
                cssClass: 'modal-popup mybets-popup',
                showPageHeader: true,
                showDialogHeader: true,
                fit: true,
                title: this.sitecore.globalMessages.MyBetsTitle,
                hideCloseButton: false,
                animation: this.getAnimation('myBets'),
                isModalDialog: true,
                showBackNav: false,
                closePrevious: false,
                backdropClass: 'hide-background-overlay',
                needsAuthentication: true,
                scrollTargetQuerySelector: '.modal-body',
                headerSelector: DEFAULT_HEADER_SELECTOR,
                gestures: true,
            },
        });
        this.popupManager.configure('bet-modal', this.popupDict['bet-modal'], {
            data: {
                proxy: this.betModalComponentLoaderService.getBetModalComponent(),
            },
            settings: {
                cssClass: `modal-popup ${epcotClass} mybets-popup betslip-vars-scope betmodal-popup`,
                isModalDialog: true,
                headerSelector: 'bs-digital-balance-header',
                scrollTargetQuerySelector: '.betslip-scroller',
                animation: this.getAnimation('bet-modal'),
                gestures: true,
                closePrevious: false,
                afterResulted: () => {
                    this.store.dispatch(BetslipResultActions.close());
                    this.store.dispatch(DigitalSettingsActions.closeSettings());
                },
            },
        });
        this.popupManager.configure('event-switcher', this.popupDict['event-switcher'], {
            data: {
                proxy: this.eventSwitcherLoader.getEventSwitcherComponent(),
            },
            settings: {
                cssClass: `modal-popup event-switcher-popup`,
                showDialogHeader: true,
                hideCloseButton: false,
                animation: DialogAnimation.SlideInFromBottom,
                scrollTargetQuerySelector: 'ms-event-switcher',
                headerSelector: DEFAULT_HEADER_SELECTOR,
                gestures: true,
            },
        });
        // preload after lcp
        this.runAfterLcp.runAfterLcp(() => {
            this.timerService.scheduleIdleCallback(async () => {
                await Promise.all(Object.entries(this.popupDict).map(([key, loader]) => loader() as Promise<unknown>));
            });
        });
    }

    private getAnimation(popup: string): DialogAnimation {
        if (this.popupConfig.slidesFromBottom[popup]) {
            return DialogAnimation.SlideInFromBottom;
        }
        if (this.popupConfig.slidesFromRight[popup]) {
            return DialogAnimation.SlideInFromRight;
        }

        return DialogAnimation.SlideInFromBottom;
    }

    private shouldShowDialogHeader(): boolean {
        return this.epcotConfig.isEnabled && this.deviceService.isMobile ? this.betFinderConfig.popupHeaderOverride : true;
    }
}
